import { isObjectV2 } from '@mimir/primitive/type-guards/isObject';
import { clamp } from '@mimir/primitive/math/clamp';
import { createUuid } from '@mimir/common/util/createUuid';
import { isInstanceOfSafeHtml, type SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import './tooltipDirective.css';

interface TooltipOptions {
  disabled?: boolean,
  nudgeRight?: number,
  nudgeBottom?: number,
  openDelay?: number,
  maxWidth?: number,
}

export interface TooltipDirectiveConfig extends TooltipOptions {
  text: string,
  html?: SafeHtmlBuilder,
}

// What a template may bind to `v-tooltip`, checked by vue-tsc through @types/tooltip-directive.d.ts; null and undefined mean no tooltip.
export type TooltipDirectiveValue =
  | string
  | number
  | SafeHtmlBuilder
  | (TooltipOptions & ({ text?: string | null, html?: never } | { html: SafeHtmlBuilder, text?: never }))
  | null
  | undefined;

export interface Nudge {
  right: number;
  bottom: number;
}

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

// Matches both Vue 2's and Vue 3's directive binding; `value` is absent for a bare `v-tooltip`.
export interface TooltipBinding {
  value?: unknown;
  arg?: string | null;
}

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface TooltipPlacement {
  left: number;
  top: number;
  position: TooltipPosition;
}

const DEFAULT_OPEN_DELAY_MS = 100;
const ACTIVATOR_GAP_PX = 10;
const VIEWPORT_MARGIN_PX = 4;
// Must match the m-tooltip-leave animation duration in tooltipDirective.css.
const LEAVE_ANIMATION_MS = 75;
export const TOOLTIP_CLASS = 'm-tooltip';
export const TOOLTIP_LEAVING_CLASS = 'm-tooltip--leaving';

// Numbers render like {{ }} interpolation does; anything else that is not a string means no tooltip.
function toTooltipText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

export function getTooltipDirectiveConfig(value: unknown): TooltipDirectiveConfig {
  if (isInstanceOfSafeHtml(value)) {
    return { text: '', html: value, disabled: false };
  }
  if (isObjectV2(value)) {
    const html = value['html'];
    return {
      text: toTooltipText(value['text']),
      disabled: Boolean(value['disabled']),
      ...(typeof value['nudgeRight'] === 'number' && { nudgeRight: value['nudgeRight'] }),
      ...(typeof value['nudgeBottom'] === 'number' && { nudgeBottom: value['nudgeBottom'] }),
      ...(typeof value['openDelay'] === 'number' && { openDelay: value['openDelay'] }),
      ...(typeof value['maxWidth'] === 'number' && { maxWidth: value['maxWidth'] }),
      ...(isInstanceOfSafeHtml(html) && { html }),
    };
  }

  return {
    text: toTooltipText(value),
    disabled: false,
  };
}

export function getTooltipPosition(arg: string | null | undefined): TooltipPosition {
  return arg === 'top' || arg === 'left' || arg === 'right' ? arg : 'bottom';
}

const OPPOSITE: Record<TooltipPosition, TooltipPosition> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

function placeOnSide(anchor: Rect, tip: Size, position: TooltipPosition): { left: number, top: number } {
  switch (position) {
    case 'top': return { left: anchor.left + (anchor.width - tip.width) / 2, top: anchor.top - tip.height - ACTIVATOR_GAP_PX };
    case 'bottom': return { left: anchor.left + (anchor.width - tip.width) / 2, top: anchor.top + anchor.height + ACTIVATOR_GAP_PX };
    case 'left': return { left: anchor.left - tip.width - ACTIVATOR_GAP_PX, top: anchor.top + (anchor.height - tip.height) / 2 };
    case 'right': return { left: anchor.left + anchor.width + ACTIVATOR_GAP_PX, top: anchor.top + (anchor.height - tip.height) / 2 };
  }
}

function overflowsOnSide(placement: { left: number, top: number }, tip: Size, viewport: Size, position: TooltipPosition): boolean {
  switch (position) {
    case 'top': return placement.top < VIEWPORT_MARGIN_PX;
    case 'bottom': return placement.top + tip.height > viewport.height - VIEWPORT_MARGIN_PX;
    case 'left': return placement.left < VIEWPORT_MARGIN_PX;
    case 'right': return placement.left + tip.width > viewport.width - VIEWPORT_MARGIN_PX;
  }
}

// Flips to the opposite side when the requested side overflows the viewport and the opposite fits, then keeps the tooltip on screen.
export function computeTooltipPlacement(anchor: Rect, tip: Size, viewport: Size, requested: TooltipPosition, nudge: Nudge): TooltipPlacement {
  let position = requested;
  let placement = placeOnSide(anchor, tip, position);
  if (overflowsOnSide(placement, tip, viewport, position)) {
    const flipped = placeOnSide(anchor, tip, OPPOSITE[position]);
    if (!overflowsOnSide(flipped, tip, viewport, OPPOSITE[position])) {
      position = OPPOSITE[position];
      placement = flipped;
    }
  }
  return {
    left: clamp(placement.left + nudge.right, VIEWPORT_MARGIN_PX, viewport.width - tip.width - VIEWPORT_MARGIN_PX),
    top: clamp(placement.top + nudge.bottom, VIEWPORT_MARGIN_PX, viewport.height - tip.height - VIEWPORT_MARGIN_PX),
    position,
  };
}

interface PlacementInputs {
  anchor: Rect;
  position: TooltipPosition;
  nudge: Nudge;
  maxWidth: number | undefined;
}

function samePlacementInputs(a: PlacementInputs, b: PlacementInputs): boolean {
  return a.anchor.left === b.anchor.left && a.anchor.top === b.anchor.top
    && a.anchor.width === b.anchor.width && a.anchor.height === b.anchor.height
    && a.position === b.position && a.nudge.right === b.nudge.right && a.nudge.bottom === b.nudge.bottom
    && a.maxWidth === b.maxWidth;
}

function placeTooltip(window: Window, tooltip: HTMLElement, inputs: PlacementInputs) {
  tooltip.style.maxWidth = inputs.maxWidth === undefined ? '' : `${inputs.maxWidth}px`;
  const placement = computeTooltipPlacement(
    inputs.anchor,
    { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
    { width: window.innerWidth, height: window.innerHeight },
    inputs.position,
    inputs.nudge,
  );
  tooltip.style.left = `${placement.left}px`;
  tooltip.style.top = `${placement.top}px`;
  tooltip.dataset['position'] = placement.position;
}

function addDescribedBy(el: HTMLElement, id: string) {
  const current = el.getAttribute('aria-describedby');
  el.setAttribute('aria-describedby', current === null || current.trim() === '' ? id : `${current} ${id}`);
}

function removeDescribedBy(el: HTMLElement, id: string) {
  const current = el.getAttribute('aria-describedby');
  if (current === null) return;
  const rest = current.split(/\s+/).filter(token => token !== '' && token !== id);
  if (rest.length === 0) {
    el.removeAttribute('aria-describedby');
  } else {
    el.setAttribute('aria-describedby', rest.join(' '));
  }
}

interface ActivatorState {
  el: HTMLElement;
  config: TooltipDirectiveConfig;
  position: TooltipPosition;
  // Set by a click, context menu or Escape; cleared when the pointer or focus leaves the activator.
  suppressed: boolean;
}

interface ShownTooltip {
  activator: ActivatorState;
  element: HTMLElement;
  // The builder whose markup is shown, or undefined for text; a builder is serialized only when the instance changes.
  html: SafeHtmlBuilder | undefined;
  written: string | null;
  placedWith: PlacementInputs;
}

function isInactive(activator: ActivatorState): boolean {
  const hasContent = activator.config.html !== undefined ? !activator.config.html.isEmpty() : activator.config.text !== '';
  return Boolean(activator.config.disabled) || !hasContent || activator.suppressed;
}

function placementInputs(activator: ActivatorState): PlacementInputs {
  return {
    anchor: activator.el.getBoundingClientRect(),
    position: activator.position,
    nudge: { right: activator.config.nudgeRight ?? 0, bottom: activator.config.nudgeBottom ?? 0 },
    maxWidth: activator.config.maxWidth,
  };
}

// Writes the configured text or builder-escaped HTML into a shown tooltip and returns whether it changed.
function writeContent(tip: ShownTooltip, config: TooltipDirectiveConfig): boolean {
  if (config.html === undefined) {
    if (tip.html === undefined && tip.written === config.text) return false;
    tip.element.textContent = config.text;
    tip.html = undefined;
    tip.written = config.text;
    return true;
  }
  if (config.html === tip.html) return false;
  const markup = config.html.getSafeHtml();
  const changed = tip.html === undefined || markup !== tip.written;
  tip.html = config.html;
  if (changed) {
    tip.element.innerHTML = markup;
    tip.written = markup;
  }
  return changed;
}

/*
  The `v-tooltip` directive of both the Vue 2 and the Vue 3 build:
  `v-tooltip:top="text"` or `v-tooltip="{ text, disabled, openDelay, nudgeRight, nudgeBottom, maxWidth }"`.
  Markup goes in as a SafeHtmlBuilder, `v-tooltip="builder"` or `{ html: builder }`, rendered as its
  escaped HTML like `v-safe-html`. A disabled button receives no pointer events, so a tooltip that
  must also show while it is disabled goes on a wrapping element instead.

  An idle activator costs one WeakMap entry: a single set of window listeners serves all
  activators, and the tooltip element exists only while shown. Vuetify 3's own directive
  instead mounts an eager VTooltip per activator and re-renders it on every update of the
  owning component, which does not scale to lists with hundreds of tooltips.
*/
export function createTooltipDirective(window: Window) {
  const document = window.document;
  const activators = new WeakMap<Element, ActivatorState>();
  let mountedCount = 0;
  // The innermost activator under the pointer.
  let hovered: ActivatorState | null = null;
  let pending: { activator: ActivatorState, timer: ReturnType<typeof setTimeout> } | null = null;
  // Only one tooltip is shown at a time; opening another closes it.
  let shown: ShownTooltip | null = null;
  let refreshFrame: number | null = null;

  function findActivator(target: EventTarget | null): ActivatorState | null {
    for (let node = target instanceof Element ? target : null; node !== null; node = node.parentElement) {
      const activator = activators.get(node);
      if (activator !== undefined) return activator;
    }
    return null;
  }

  function cancelOpen(activator: ActivatorState) {
    if (pending?.activator === activator) {
      clearTimeout(pending.timer);
      pending = null;
    }
  }

  function close(animate: boolean) {
    if (shown === null) return;
    const { activator, element } = shown;
    shown = null;
    if (refreshFrame !== null) {
      window.cancelAnimationFrame(refreshFrame);
      refreshFrame = null;
    }
    removeDescribedBy(activator.el, element.id);
    if (animate) {
      // Removed on a timer rather than on animationend, which never fires when reduced motion disables the animation.
      element.classList.add(TOOLTIP_LEAVING_CLASS);
      element.removeAttribute('id');
      setTimeout(() => element.remove(), LEAVE_ANIMATION_MS);
    } else {
      element.remove();
    }
  }

  function closeFor(activator: ActivatorState) {
    if (shown?.activator === activator) {
      close(true);
    }
  }

  // A tooltip whose activator left the document without unmounting, as in a deactivated <KeepAlive>, would otherwise stay open.
  function closeIfDetached() {
    if (shown !== null && !shown.activator.el.isConnected) {
      close(true);
    }
  }

  function open(activator: ActivatorState) {
    pending = null;
    if (!activator.el.isConnected || isInactive(activator)) return;
    close(true);
    const element = document.createElement('div');
    element.classList.add(TOOLTIP_CLASS);
    element.id = `m-tooltip-${createUuid()}`;
    element.setAttribute('role', 'tooltip');
    document.body.appendChild(element);
    const inputs = placementInputs(activator);
    const tip: ShownTooltip = { activator, element, html: undefined, written: null, placedWith: inputs };
    writeContent(tip, activator.config);
    placeTooltip(window, element, inputs);
    addDescribedBy(activator.el, element.id);
    shown = tip;
  }

  function scheduleOpen(activator: ActivatorState) {
    if (isInactive(activator) || shown?.activator === activator || pending?.activator === activator) return;
    if (pending !== null) {
      clearTimeout(pending.timer);
    }
    pending = { activator, timer: setTimeout(() => open(activator), activator.config.openDelay ?? DEFAULT_OPEN_DELAY_MS) };
  }

  // Applies the updates of one frame together, outside the owning component's patch, and measures only when placement inputs changed.
  function refresh() {
    refreshFrame = null;
    if (shown === null) return;
    if (!shown.activator.el.isConnected) {
      close(true);
      return;
    }
    const contentChanged = writeContent(shown, shown.activator.config);
    const inputs = placementInputs(shown.activator);
    if (contentChanged || !samePlacementInputs(inputs, shown.placedWith)) {
      shown.placedWith = inputs;
      placeTooltip(window, shown.element, inputs);
    }
  }

  function leave(activator: ActivatorState) {
    activator.suppressed = false;
    cancelOpen(activator);
    closeFor(activator);
  }

  // A trigger that opens a menu leaves the pointer in place, so suppression holds until the pointer leaves or focus moves out.
  function suppress(activator: ActivatorState) {
    activator.suppressed = true;
    cancelOpen(activator);
    closeFor(activator);
  }

  function setHovered(activator: ActivatorState | null) {
    if (activator === hovered) return;
    if (hovered !== null) {
      leave(hovered);
    }
    hovered = activator;
    if (activator !== null) {
      scheduleOpen(activator);
    }
  }

  function onMouseOver(event: MouseEvent) {
    closeIfDetached();
    setHovered(findActivator(event.target));
  }

  // Leaving the document fires a mouseout without a related target and no mouseover.
  function onMouseOut(event: MouseEvent) {
    if (event.relatedTarget === null) {
      setHovered(null);
    }
  }

  // Keyboard focus only: a mouse click also focuses, and the click already suppresses the tooltip.
  function onFocusIn(event: FocusEvent) {
    if (event.target instanceof Element && event.target.matches(':focus-visible')) {
      const activator = findActivator(event.target);
      if (activator !== null) {
        scheduleOpen(activator);
      }
    }
  }

  function onFocusOut(event: FocusEvent) {
    const activator = findActivator(event.target);
    const stillInside = event.relatedTarget instanceof Node && activator?.el.contains(event.relatedTarget);
    if (activator !== null && !stillInside) {
      leave(activator);
    }
  }

  // Escape dismisses the tooltip wherever focus is, so that a hover-opened tooltip can be dismissed too.
  function onKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    if (pending !== null) {
      suppress(pending.activator);
    }
    if (shown !== null) {
      suppress(shown.activator);
    }
  }

  function onClick(event: MouseEvent) {
    const activator = findActivator(event.target);
    if (activator !== null) {
      suppress(activator);
    }
  }

  // Scrolling the anchor's own scroll container, or the document, moves it away from its tooltip; scrolling an unrelated panel does not.
  function onScroll(event: Event) {
    if (shown === null) return;
    const anchor = shown.activator.el;
    const target = event.target;
    if (!anchor.isConnected || !(target instanceof Node) || target.contains(anchor) || anchor.contains(target)) {
      close(true);
    }
  }

  function onResize() {
    close(true);
  }

  // Capture phase, so that handlers in the app that stop propagation cannot hide events from the directive.
  function addListeners() {
    window.addEventListener('mouseover', onMouseOver, true);
    window.addEventListener('mouseout', onMouseOut, true);
    window.addEventListener('focusin', onFocusIn, true);
    window.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('contextmenu', onClick, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('wheel', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onResize);
  }

  function removeListeners() {
    window.removeEventListener('mouseover', onMouseOver, true);
    window.removeEventListener('mouseout', onMouseOut, true);
    window.removeEventListener('focusin', onFocusIn, true);
    window.removeEventListener('focusout', onFocusOut, true);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('click', onClick, true);
    window.removeEventListener('contextmenu', onClick, true);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('wheel', onScroll, true);
    window.removeEventListener('resize', onResize);
  }

  return {
    mounted(el: HTMLElement, binding: TooltipBinding) {
      activators.set(el, {
        el,
        config: getTooltipDirectiveConfig(binding.value),
        position: getTooltipPosition(binding.arg),
        suppressed: false,
      });
      mountedCount++;
      if (mountedCount === 1) {
        addListeners();
      }
    },
    updated(el: HTMLElement, binding: TooltipBinding) {
      const activator = activators.get(el);
      if (activator === undefined) return;
      activator.config = getTooltipDirectiveConfig(binding.value);
      activator.position = getTooltipPosition(binding.arg);
      if (shown?.activator !== activator) return;
      if (isInactive(activator)) {
        close(true);
      } else if (refreshFrame === null) {
        refreshFrame = window.requestAnimationFrame(refresh);
      }
    },
    // The tooltip lives under <body>, outside the unmounted subtree, so it is removed here rather than by Vue.
    beforeUnmount(el: HTMLElement) {
      const activator = activators.get(el);
      if (activator === undefined) return;
      cancelOpen(activator);
      if (shown?.activator === activator) {
        close(false);
      }
      if (hovered === activator) {
        hovered = null;
      }
      activators.delete(el);
      mountedCount--;
      if (mountedCount === 0) {
        removeListeners();
      }
    },
  };
}
